// src/layout/builders/Payment/ProjectPaymentHistory.jsx
import React, { useEffect } from "react";
import usePaymentStore from "../../../store/builders/usePaymentStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
  CheckCircle,
  CreditCard,
  IndianRupee,
  Clock,
  HelpCircle,
} from "lucide-react"; // Added icons

const statusClasses = {
  Paid: "text-green-600",
  Failed: "text-red-600", // Though likely all entries here will be "Paid"
  Pending: "text-yellow-600",
};

const formatDisplayDateTime = (isoDateString) => {
  if (!isoDateString) return "N/A";
  try {
    return new Date(isoDateString)
      .toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  } catch (e) {
    return "Invalid Date";
  }
};

const paymentStatusDisplayInfo = (status) => {
  switch (status?.toLowerCase()) {
    case "paid":
      return {
        icon: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
        text: "Paid",
        textClass: "text-green-600",
        bgClass: "bg-green-50",
      };
    // Add other statuses if your project_transactions table can have them
    default:
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-500" />,
        text: status || "Unknown",
        textClass: "text-gray-600",
        bgClass: "bg-gray-50",
      };
  }
};

// Mobile Transaction Card Component
const TransactionCard = ({ transaction, index }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow">
    {/* Transaction Header */}
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">
            {formatDisplayDateTime(transaction.transaction_date)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Ref ID:</span>
          <span className="text-xs font-medium text-gray-700 truncate">
            {transaction.transaction_reference_id || "N/A"}
          </span>
        </div>
      </div>
      <div className="flex-shrink-0">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
            paymentStatusDisplayInfo(transaction.transaction_status).textClass
          } ${
            paymentStatusDisplayInfo(transaction.transaction_status).bgClass
          }`}
        >
          {paymentStatusDisplayInfo(transaction.transaction_status).icon}
          <span className="ml-1">
            {transaction.transaction_status || "N/A"}
          </span>
        </span>
      </div>
    </div>

    {/* Transaction Details */}
    <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
      <div className="flex items-center gap-2">
        <CreditCard className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-gray-500">Method</p>
          <p className="font-medium truncate">
            {transaction.payment_method || "N/A"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <IndianRupee className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-gray-500">Amount</p>
          <p className="font-semibold text-gray-800">
            {parseFloat(transaction.amount).toLocaleString("en-IN")}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ProjectPaymentHistory = ({ projectId }) => {
  const {
    projectTransactions,
    isProjectTransactionsLoading,
    fetchProjectTransactions,
  } = usePaymentStore();

  useEffect(() => {
    if (projectId) {
      fetchProjectTransactions(projectId);
    }
  }, [projectId, fetchProjectTransactions]);

  if (isProjectTransactionsLoading && projectTransactions.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <AiOutlineLoading3Quarters className="animate-spin text-2xl text-blue-500" />
        <p className="ml-2 text-gray-500 text-sm">Loading payment history...</p>
      </div>
    );
  }

  if (!isProjectTransactionsLoading && projectTransactions.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500 text-sm">
        No payment history found for this project.
      </div>
    );
  }

  return (
    <div className="p-3 lg:p-4 bg-white shadow-md rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg lg:text-xl font-semibold">Payment History</h2>
      </div>

      {/* Mobile Card View */}
      <div className="block lg:hidden">
        <div className="space-y-4">
          {projectTransactions.map((transaction, index) => (
            <TransactionCard
              key={transaction.id || index}
              transaction={transaction}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="rounded-lg overflow-hidden border border-gray-200">
            {/* Heading Row */}
            <div className="grid grid-cols-[180px_1fr_200px_160px_160px] items-center bg-gray-50 px-6 py-3 font-semibold text-xs text-gray-700 border-b border-gray-200">
              <span>Date & Time</span>
              <span>Payment Method</span>
              <span>Transaction Reference ID</span>
              <span className="justify-end flex">Amount</span>
              <span className="justify-end flex">Status</span>
            </div>
            {/* Data Rows */}
            {projectTransactions.map((transaction, index) => {
              const statusInfo = paymentStatusDisplayInfo(
                transaction.transaction_status
              );
              return (
                <div
                  key={transaction.id || index}
                  className="grid grid-cols-[180px_1fr_200px_160px_160px] items-center bg-white px-6 py-3 border-b border-gray-100 text-sm font-medium"
                >
                  {/* Date/Time */}
                  <span className="text-gray-600 flex items-center">
                    <Clock className="w-3 h-3 mr-1 text-gray-400" />
                    {formatDisplayDateTime(transaction.transaction_date)}
                  </span>
                  {/* Payment Method */}
                  <span className="text-gray-800 truncate">
                    {transaction.payment_method || "-"}
                  </span>
                  {/* Reference ID */}
                  <span className="text-gray-800 truncate">
                    {transaction.transaction_reference_id || "-"}
                  </span>
                  {/* Amount */}
                  <span className="font-semibold text-gray-900 flex items-center justify-end text-base">
                    <IndianRupee className="w-4 h-4 mr-1 text-gray-700" />
                    {parseFloat(transaction.amount).toLocaleString("en-IN")}
                  </span>
                  {/* Status */}
                  <span
                    className={`flex justify-end items-center text-xs font-semibold px-2 py-1 rounded ${statusInfo.textClass} ${statusInfo.bgClass}`}
                  >
                    {statusInfo.icon}
                    <span className="ml-1">
                      {transaction.transaction_status || "N/A"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPaymentHistory;
